import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";

/**
 * ProtectedRoute
 * - Requiere sesión
 * - (Opcional) valida roles
 * - Gate opcional de foto:
 *     La foto de identificación SOLO se exige después de aceptar los Términos y Condiciones (modal del Home).
 */
const ProtectedRoute = ({ children, requiredRoles = [], skipPhotoGate = false }) => {
  const { isAuthenticated, loading, user, hasRole, currentRole, isCameraRegistrationActive } = useAuth();
  const location = useLocation();

  // Mientras inicializa auth (/verify / refresh), no decidimos nada.
  if (loading) return <LoadingSpinner message="Cargando..." />;

  // Sin sesión => login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Evita redirecciones prematuras: a veces isAuthenticated=true unos ms antes de setUser.
  if (!user) {
    return <LoadingSpinner message="Cargando perfil..." />;
  }

  // ===== Helpers =====
  const pathname = String(location?.pathname || "");
  const isIdentificacionRoute = pathname.startsWith("/identificacion");
  const isRootRoute = pathname === "/";

  const roleLower = String(currentRole || "").toLowerCase();

  // ✅ incluye admin_facultad como staff/admin
  const isAdmin =
    roleLower === "administrador" ||
    roleLower === "admin" ||
    roleLower === "superadmin" ||
    roleLower === "admin_facultad";

  // ✅ docente/asistente (si tienes otros roles tipo "coordinador", agrégalos aquí)
  const isTeacher =
    roleLower === "docente" ||
    roleLower === "asistente";

  // ✅ estudiante SOLO si el rol actual ES estudiante
  const isStudent = roleLower === "estudiante";

  const hasAuthPhoto = Boolean(
    user?.auth_foto?.public_url ||
    user?.auth_foto?.url ||
    user?.auth_foto_url ||
    user?.authFotoUrl
  );

  // ✅ MISMA clave que Home (PlatformIntroModal): si no está, aún NO se aceptaron TyC.
  const introKey = (() => {
    const uid = user?.id || user?.email || user?.correo || "anon";
    return `intro:platform:v1:${uid}`;
  })();

  const hasAcceptedTerms = (() => {
    try {
      return localStorage.getItem(introKey) === "1";
    } catch {
      return false;
    }
  })();

  // ===== Gate: Términos y Condiciones (solo estudiantes) =====
  // Si el estudiante NO ha aceptado TyC, lo llevamos al Home para que vea el modal.
  if (isStudent && !hasAcceptedTerms && !isRootRoute) {
    const toPath = "/";
    if (pathname !== toPath) return <Navigate to={toPath} replace />;
    return children;
  }

  // ===== Gate: Foto (solo después de aceptar TyC) =====
  const isCameraActive = isCameraRegistrationActive ? isCameraRegistrationActive() : true;

  if (isIdentificacionRoute && !isCameraActive) {
    return <Navigate to="/" replace />;
  }

  if (
    isCameraActive &&
    !skipPhotoGate &&
    isStudent &&
    hasAcceptedTerms &&
    !isIdentificacionRoute &&
    !isRootRoute &&
    !hasAuthPhoto
  ) {
    return <Navigate to="/identificacion" state={{ from: location }} replace />;
  }

  // ===== Roles =====
  if (requiredRoles.length > 0) {
    const ok = requiredRoles.some((r) => (hasRole ? hasRole(r) : false));
    if (!ok) {
      return (
        <div className="access-denied" style={{ padding: 24 }}>
          <h2>Acceso Denegado</h2>
          <p>No tienes permisos para acceder a esta página.</p>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
